import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Sparkles,
  User,
  Building2,
  Briefcase,
  Mail,
  Phone,
  Linkedin,
  FileDown,
  Calendar,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  Lock,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Shield,
  Clock,
  MapPin,
} from 'lucide-react';
import { resolveBadgeCode, revealBadgeContact, saveConnectionNote, BadgeResolveResponse } from '../lib/api';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { ROLE_THEMES, ParticipantRole } from '../lib/roles';

export const BadgeResolverPage: React.FC = () => {
  const { badgeCode } = useParams<{ badgeCode: string }>();
  const navigate = useNavigate();

  // Authentication State of the viewer/scanner
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [authEmailInput, setAuthEmailInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Badge Resolver State
  const [resolveData, setResolveData] = useState<BadgeResolveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Contact Revelation State
  const [contactRevealed, setContactRevealed] = useState(false);
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [revealingContact, setRevealingContact] = useState(false);

  // Discussion Note State
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  // 1. Initial Authentication Check
  useEffect(() => {
    async function checkAuth() {
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user?.email) {
          const email = data.session.user.email.toLowerCase().trim();
          setSessionEmail(email);
          return email;
        }
      }
      const savedEmail =
        sessionStorage.getItem('iiab_portal_email') || sessionStorage.getItem('iiab_participant_email');
      if (savedEmail) {
        setSessionEmail(savedEmail);
        return savedEmail;
      }
      return null;
    }

    checkAuth().then((email) => {
      if (badgeCode) {
        loadBadge(badgeCode, email || undefined);
      }
    });
  }, [badgeCode]);

  // 2. Fetch Badge Profile from Backend
  async function loadBadge(code: string, email?: string) {
    setLoading(true);
    setErrorMessage(null);
    try {
      const data = await resolveBadgeCode(code, email);
      setResolveData(data);

      if (data.authenticated && data.profile) {
        if (data.profile.contact) {
          setContactRevealed(true);
          setContactEmail(data.profile.contact.email || null);
          setContactPhone(data.profile.contact.phone || null);
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Unable to scan or resolve this badge credential.');
    } finally {
      setLoading(false);
    }
  }

  // 3. Handle Send Magic Link / OTP for Anonymous Scanners
  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!authEmailInput || !authEmailInput.includes('@')) {
      setAuthError('Please enter a valid email address.');
      return;
    }

    setAuthLoading(true);
    setAuthError(null);

    try {
      const email = authEmailInput.trim().toLowerCase();
      if (supabase && isSupabaseConfigured) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/b/${badgeCode}`,
          },
        });
        if (error) throw error;
        setIsOtpSent(true);
      } else {
        // Fallback for dev / hermetic tests
        sessionStorage.setItem('iiab_participant_email', email);
        sessionStorage.setItem('iiab_portal_email', email);
        setSessionEmail(email);
        if (badgeCode) {
          loadBadge(badgeCode, email);
        }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to dispatch magic link. Please check your email.');
    } finally {
      setAuthLoading(false);
    }
  }

  // 4. Handle OTP Code Verification
  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) {
      setAuthError('Please enter the 6-digit code sent to your email.');
      return;
    }

    setAuthLoading(true);
    setAuthError(null);

    try {
      if (supabase) {
        const { data, error } = await supabase.auth.verifyOtp({
          email: authEmailInput.trim().toLowerCase(),
          token: otpCode.trim(),
          type: 'email',
        });
        if (error) throw error;
        if (data.session?.user?.email) {
          const email = data.session.user.email.toLowerCase().trim();
          setSessionEmail(email);
          sessionStorage.setItem('iiab_participant_email', email);
          sessionStorage.setItem('iiab_portal_email', email);
          if (badgeCode) {
            loadBadge(badgeCode, email);
          }
        }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Invalid or expired 6-digit code.');
    } finally {
      setAuthLoading(false);
    }
  }

  // 5. Handle Contact Revelation
  async function handleRevealContact() {
    if (!badgeCode || !sessionEmail) return;
    setRevealingContact(true);
    try {
      const res = await revealBadgeContact(badgeCode, sessionEmail);
      setContactRevealed(true);
      setContactEmail(res.email || null);
      setContactPhone(res.phone || null);
    } catch (err: any) {
      alert(err.message || 'Failed to retrieve contact details.');
    } finally {
      setRevealingContact(false);
    }
  }

  // 6. Handle Save Discussion Note
  async function handleSaveNote() {
    if (!resolveData?.profile?.id || !note.trim()) return;
    setSavingNote(true);
    try {
      const viewerId = (resolveData as any).viewerAttendeeId || sessionEmail || 'viewer';
      await saveConnectionNote(viewerId, resolveData.profile.id, note.trim());
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
    } finally {
      setSavingNote(false);
    }
  }

  // 7. Download vCard (.vcf)
  function handleDownloadVCard() {
    if (!resolveData?.profile) return;
    const p = resolveData.profile;

    const vCardLines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${p.fullName}`,
      p.companyName ? `ORG:${p.companyName}` : '',
      p.jobTitle ? `TITLE:${p.jobTitle}` : '',
      contactEmail ? `EMAIL;TYPE=INTERNET,WORK:${contactEmail}` : '',
      contactPhone ? `TEL;TYPE=CELL,WORK:${contactPhone}` : '',
      p.linkedinUrl ? `URL;TYPE=LinkedIn:${p.linkedinUrl}` : '',
      'NOTE:Met at India International Agri Business Expo (IIAB 2027) - Pavilion 3, JWCC Mumbai',
      'END:VCARD',
    ]
      .filter(Boolean)
      .join('\r\n');

    const blob = new Blob([vCardLines], { type: 'text/vcard;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${p.fullName.replace(/\s+/g, '_')}_IIAB2027.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // -------------------------------------------------------------
  // Render: Loading State
  // -------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mb-4" />
        <p className="text-sm font-medium text-slate-400">Verifying badge credential &amp; security status...</p>
      </div>
    );
  }

  // -------------------------------------------------------------
  // Render: Replaced or Cancelled Badge Error State
  // -------------------------------------------------------------
  if (resolveData?.status === 'replaced' || resolveData?.status === 'cancelled') {
    const isReplaced = resolveData.status === 'replaced';
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-2xl bg-rose-950/80 border border-rose-800 text-rose-400 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-rose-950 text-rose-300 border border-rose-800 mb-3">
            {isReplaced ? 'Badge Replaced' : 'Badge Inactive'}
          </span>
          <h2 className="text-xl font-bold text-white mb-2">
            {isReplaced ? 'This badge has been replaced' : 'This credential has been cancelled'}
          </h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-6">
            {isReplaced
              ? 'A newer badge was re-issued for this attendee at the Registration Desk. Please ask the participant to present their updated badge or open their My IIAB digital pass.'
              : 'This credential is no longer active for India International Agri Business Expo 2027.'}
          </p>
          <Link
            to="/my-iiab"
            className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold text-xs transition-colors"
          >
            <span>Open My IIAB Portal</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // Render: Credential Not Found
  // -------------------------------------------------------------
  if (errorMessage || !resolveData) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-950/80 border border-amber-800 text-amber-400 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Credential Not Recognized</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-6">
            {errorMessage || 'The scanned badge code was not found in our live registry.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-semibold text-xs transition-colors"
          >
            <span>Return to Event Homepage</span>
          </Link>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // Render: ANONYMOUS SCANNER GATE (No Personal Data Revealed!)
  // -------------------------------------------------------------
  if (!resolveData.authenticated || resolveData.requiresLogin) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800/90 border border-slate-700 rounded-3xl p-8 shadow-2xl backdrop-blur-md">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0A3D27] to-emerald-700 flex items-center justify-center shadow-lg border border-emerald-500/20 mb-3">
              <Lock className="w-7 h-7 text-[#FAF5EA]" />
            </div>
            <h1 className="text-xl font-black text-white">Sign In to View Contact</h1>
            <p className="text-xs text-slate-400 mt-1">
              Under India's DPDP Act, contact details and professional bios at IIAB 2027 are reserved exclusively for
              registered participants.
            </p>
          </div>

          {authError && (
            <div className="mb-4 p-3 rounded-xl bg-rose-950/60 border border-rose-800 text-rose-200 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <span>{authError}</span>
            </div>
          )}

          {!isOtpSent ? (
            <form onSubmit={handleSendMagicLink} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Enter Your Registered Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    value={authEmailInput}
                    onChange={(e) => setAuthEmailInput(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
              >
                {authLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>Verify &amp; Connect</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="p-3 bg-emerald-950/40 border border-emerald-800/60 rounded-xl text-emerald-300 text-xs text-center">
                We sent a 6-digit login code to <strong>{authEmailInput}</strong>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  6-Digit Verification Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-center text-xl font-mono tracking-widest text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
              >
                {authLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <span>Sign In &amp; View Contact</span>}
              </button>

              <button
                type="button"
                onClick={() => setIsOtpSent(false)}
                className="w-full text-xs text-slate-400 hover:text-slate-200 text-center block pt-2"
              >
                ← Back to email input
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-slate-700/60 text-center text-xs text-slate-500">
            Attending IIAB for the first time?{' '}
            <Link to="/register" className="text-emerald-400 hover:underline">
              Get an attendee pass
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // Render: AUTHENTICATED SCANNER (Profile + Mutual Connection)
  // -------------------------------------------------------------
  const profile = resolveData.profile!;
  const primaryRole = (profile.primaryRole as ParticipantRole) || 'visitor';
  const roleTheme = ROLE_THEMES[primaryRole] || ROLE_THEMES.visitor;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-16">
      {/* Top Banner */}
      <header className="bg-slate-900 text-white border-b border-slate-800 py-3.5 px-4 shadow-md sticky top-0 z-30">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/my-iiab" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <span className="text-xs font-bold tracking-tight text-white uppercase">IIAB 2027 Connect</span>
          </Link>
          <Link
            to="/my-iiab"
            className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
          >
            <span>My IIAB Portal</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Main Profile Card Container */}
      <main className="max-w-xl mx-auto px-4 pt-8 space-y-6">
        {/* Mutual Connection Recorded Notice */}
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2.5 shadow-xs">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <strong>Mutual Connection Recorded!</strong>
            <p className="text-[11px] text-emerald-700 mt-0.5">
              This interaction has been saved to your "My Connections" directory in My IIAB.
            </p>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-md">
          {/* Avatar & Roles */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
            <div className="w-24 h-24 rounded-2xl bg-slate-100 border-2 border-slate-200 overflow-hidden shrink-0 flex items-center justify-center shadow-xs">
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt={profile.fullName} className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-slate-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1.5">
                <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${roleTheme.badge}`}>
                  {roleTheme.label}
                </span>
                {profile.roles?.map((r: string) =>
                  r !== primaryRole ? (
                    <span
                      key={r}
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                    >
                      {r.replace('_', ' ')}
                    </span>
                  ) : null
                )}
              </div>

              <h2 className="text-2xl font-black text-slate-900 tracking-tight">{profile.fullName}</h2>

              {profile.jobTitle && <p className="text-sm font-medium text-slate-600">{profile.jobTitle}</p>}

              {profile.companyName && (
                <p className="text-sm font-bold text-emerald-800 mt-0.5 flex items-center justify-center sm:justify-start gap-1">
                  <Building2 className="w-3.5 h-3.5" />
                  <span>{profile.companyName}</span>
                </p>
              )}
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">About</h4>
              <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{profile.bio}</p>
            </div>
          )}

          {/* LinkedIn Profile */}
          {profile.linkedinUrl && (
            <div className="mt-4 pt-3">
              <a
                href={profile.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900"
              >
                <Linkedin className="w-3.5 h-3.5" />
                <span>View LinkedIn Profile</span>
                <ExternalLink className="w-3 h-3 ml-0.5" />
              </a>
            </div>
          )}

          {/* Contact Information & Privacy Section */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Contact Information</h4>

            {contactRevealed && (contactEmail || contactPhone) ? (
              <div className="space-y-2.5 p-4 rounded-2xl bg-slate-50 border border-slate-200/80">
                {contactEmail && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      Email:
                    </span>
                    <a href={`mailto:${contactEmail}`} className="font-semibold text-emerald-700 hover:underline">
                      {contactEmail}
                    </a>
                  </div>
                )}
                {contactPhone && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-slate-400" />
                      Phone:
                    </span>
                    <a href={`tel:${contactPhone}`} className="font-semibold text-emerald-700 hover:underline">
                      {contactPhone}
                    </a>
                  </div>
                )}
              </div>
            ) : profile.shareContactAllowed ? (
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs text-slate-600">Contact shared with verified attendees.</span>
                <button
                  onClick={handleRevealContact}
                  disabled={revealingContact}
                  className="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-semibold transition-colors cursor-pointer"
                >
                  {revealingContact ? 'Revealing...' : 'Show Contact Info'}
                </button>
              </div>
            ) : (
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 text-xs text-slate-500 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400 shrink-0" />
                <span>This participant has chosen not to share direct phone or email details.</span>
              </div>
            )}

            {/* vCard Download Button */}
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDownloadVCard}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 text-xs font-semibold shadow-xs transition-colors cursor-pointer"
              >
                <FileDown className="w-4 h-4 text-emerald-600" />
                <span>Save to Contacts (vCard)</span>
              </button>

              <Link
                to="/my-iiab?tab=meetings"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-semibold shadow-xs transition-colors"
              >
                <Calendar className="w-4 h-4" />
                <span>Request B2B Meeting</span>
              </Link>
            </div>
          </div>

          {/* Quick Discussion Note */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Private Note on this Discussion
              </h4>
              {noteSaved && (
                <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Note Saved!
                </span>
              )}
            </div>
            <textarea
              rows={2}
              placeholder="e.g., Discussed organic fertilizer distribution for Western India..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full text-xs border border-slate-300 rounded-xl p-3 focus:outline-none focus:border-emerald-600 text-slate-800"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={handleSaveNote}
                disabled={savingNote || !note.trim()}
                className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium cursor-pointer transition-colors disabled:opacity-40"
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BadgeResolverPage;
